import React, { useEffect, useState } from 'react';
import Icon from '../../shared/Icon';
import { fmtDateTime } from '../../../utils/formatters';
import { TONE_BADGE } from '../../../utils/statusColors';
import { procurementService } from '../../../services/api';
import { ehSimulacao, JobDaLista, quandoDoJob } from '../../../utils/jobDeAutomacao';

/**
 * Atividade da proposta — transversal aos estágios: última automação, operação
 * em curso, MENSAGENS do portal (chat do pregoeiro) e o quadro informativo
 * (avisos/esclarecimentos/impugnações).
 *
 * Aqui o conteúdo é LISTADO, não só contado: um "15 avisos" que não deixa ler
 * os avisos obriga a abrir o portal por fora — e é justamente onde mora a
 * convocação que a IPCOM não pode perder.
 */

const JOB_STATUS: Record<string, { label: string; cls: string }> = {
  queued: { label: 'na fila', cls: TONE_BADGE.warning },
  running: { label: 'executando', cls: TONE_BADGE.live },
  completed: { label: 'concluído', cls: TONE_BADGE.neutral }, // feito é o normal (L5, 24/10/2026); a falha e a ação manual é que pedem olho
  failed: { label: 'falhou', cls: TONE_BADGE.danger },
  needs_manual: { label: 'ação manual', cls: TONE_BADGE.warning },
};

interface Props {
  /** Da LISTA de jobs (formato enxuto): ver utils/jobDeAutomacao. */
  latestJob?: JobDaLista | null;
  activeOp?: { label: string; currentStep: string; steps: string[]; elapsedMs: number } | null;
  item?: any | null;
  onViewJob: (job: any) => void;
  /** aberto por padrão nos estágios onde o portal fala (Disputa/Resultado) */
  defaultOpen?: boolean;
  /** avisa a página pra recarregar o item depois de buscar o quadro */
  onQuadroRefreshed?: (conteudoVeio: boolean, motivo?: string) => void;
}

export default function AtividadePanel({ latestJob, activeOp, item, onViewJob, defaultOpen, onQuadroRefreshed }: Props) {
  const sc = item?.situacaoCompra;
  const nA = Number(sc?.qtdeAvisos) || (Array.isArray(sc?.avisos) ? sc.avisos.length : 0);
  const nE = Number(sc?.qtdeEsclarecimentos) || (Array.isArray(sc?.esclarecimentos) ? sc.esclarecimentos.length : 0);
  const nI = Number(sc?.qtdeImpugnacoes) || (Array.isArray(sc?.impugnacoes) ? sc.impugnacoes.length : 0);
  const totalAvisos = nA + nE + nI;

  const [messages, setMessages] = useState<any[] | null>(null);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [buscandoQuadro, setBuscandoQuadro] = useState(false);
  const [quadroMotivo, setQuadroMotivo] = useState<string | null>(null);
  const [renovando, setRenovando] = useState(false);

  // Mensagens do portal só são buscadas quando o painel abre (é conteúdo
  // pesado e nem toda proposta tem chat).
  const loadMessages = async () => {
    if (!item?.id || messages || loadingMsgs) return;
    setLoadingMsgs(true);
    try {
      const r = await procurementService.getItemMessages(item.id);
      setMessages(Array.isArray(r) ? r : r?.data || []);
    } catch {
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => { setMessages(null); }, [item?.id]);

  // Quando o portal mandou só os contadores, o conteúdo pode ser buscado sob
  // demanda (POST items/:id/quadro-informativo/refresh).
  const buscarQuadro = async () => {
    if (!item?.id || buscandoQuadro) return;
    setBuscandoQuadro(true);
    setQuadroMotivo(null);
    try {
      const r = await procurementService.refreshQuadroInformativo(item.id);
      setQuadroMotivo(r?.motivo || null);
      onQuadroRefreshed?.(!!r?.conteudoDisponivel, r?.motivo);
    } catch (e: any) {
      setQuadroMotivo('falha_proxy');
      onQuadroRefreshed?.(false, 'falha_proxy');
    } finally {
      setBuscandoQuadro(false);
    }
  };

  // Sessão do ComprasNet expirada é a causa mais comum — e tem conserto aqui
  // mesmo, sem mandar o usuário procurar em outra tela.
  const renovarSessao = async () => {
    if (renovando) return;
    setRenovando(true);
    try {
      await procurementService.forceReloginComprasnet();
      setQuadroMotivo(null);
      await buscarQuadro();
    } catch {
      setQuadroMotivo('falha_relogin');
    } finally {
      setRenovando(false);
    }
  };

  const js = latestJob ? (JOB_STATUS[latestJob.status] || JOB_STATUS.queued) : null;
  if (!latestJob && !activeOp && !totalAvisos && !item?.id) return null;

  const renderAvisos = (list: any[] | undefined, titulo: string, cor: string) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return (
      <div>
        <div className={`text-[10px] font-semibold ${cor} uppercase tracking-wide mb-1`}>{titulo} ({list.length})</div>
        <div className="space-y-1">
          {list.map((it: any, i: number) => (
            <div key={i} className="text-[11px] text-slate-300 bg-central-elevado rounded px-2 py-1.5 border border-central-borda">
              {(it.dataHoraInclusao || it.dataHora) && (
                <span className="text-[9px] text-slate-500 mr-1.5">{fmtDateTime(it.dataHoraInclusao || it.dataHora)}</span>
              )}
              <span className="whitespace-pre-wrap">{(it.mensagem || it.texto || it.descricao || '—').toString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const temListaAvisos = Array.isArray(sc?.avisos) || Array.isArray(sc?.esclarecimentos) || Array.isArray(sc?.impugnacoes);

  return (
    <details
      className="bg-central-cartao border border-central-borda rounded-lg"
      open={defaultOpen}
      onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) loadMessages(); }}
    >
      <summary className="px-3 py-2 text-xs text-slate-400 font-medium cursor-pointer hover:text-slate-200 transition-colors flex items-center gap-2 flex-wrap">
        <Icon name="refresh" className="w-3.5 h-3.5" />
        Atividade e mensagens do portal
        {activeOp && <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE_BADGE.live}`}>em curso</span>}
        {!activeOp && js && <span className={`text-[10px] px-2 py-0.5 rounded-full ${js.cls}`}>{js.label}</span>}
        {nA > 0 && <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE_BADGE.warning}`}>{nA} aviso(s)</span>}
        {nE > 0 && <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE_BADGE.progress}`}>{nE} esclarec.</span>}
        {nI > 0 && <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE_BADGE.danger}`}>{nI} impugn.</span>}
      </summary>

      <div className="px-3 pb-3 space-y-3">
        {activeOp && (
          <div className="bg-central-elevado rounded-lg p-2.5">
            <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1">
              <span>{activeOp.label}</span>
              <span className="text-slate-500">{Math.round(activeOp.elapsedMs / 1000)}s</span>
            </div>
            <div className="h-1.5 bg-central-elevado rounded overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${Math.min(95, ((activeOp.steps.indexOf(activeOp.currentStep) + 1) / Math.max(1, activeOp.steps.length)) * 100)}%` }}
              />
            </div>
            <div className="text-[10px] text-slate-500 mt-1">{activeOp.currentStep}</div>
          </div>
        )}

        {/* Quadro informativo — CONTEÚDO, não só contagem */}
        {totalAvisos > 0 && (
          <div className="space-y-2">
            {temListaAvisos ? (
              <>
                {renderAvisos(sc.avisos, 'Avisos', 'text-amber-300')}
                {renderAvisos(sc.esclarecimentos, 'Esclarecimentos', 'text-slate-300')}
                {renderAvisos(sc.impugnacoes, 'Impugnações', 'text-red-300')}
              </>
            ) : (
              <div className="text-[11px] text-slate-400 bg-central-elevado rounded px-2 py-1.5">
                O portal informou {totalAvisos} registro(s) no quadro informativo, mas só mandou os contadores.
                {sc?.ultimoAviso?.mensagem && (
                  <div className="text-slate-300 mt-1 whitespace-pre-wrap">{sc.ultimoAviso.mensagem}</div>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={buscarQuadro}
                    disabled={buscandoQuadro || renovando}
                    className="px-2.5 py-1 rounded bg-central-cartao border border-central-borda hover:bg-central-elevado disabled:opacity-50 text-slate-200 text-[11px] transition-colors"
                  >
                    {buscandoQuadro ? 'Buscando no portal…' : 'Buscar conteúdo no portal'}
                  </button>
                  {quadroMotivo === 'sessao_expirada' && (
                    <button
                      type="button"
                      onClick={renovarSessao}
                      disabled={renovando}
                      className="px-2.5 py-1 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-50 text-[11px] transition-colors"
                    >
                      {renovando ? 'Reconectando ao ComprasNet… (até 2min)' : 'Renovar sessão do ComprasNet'}
                    </button>
                  )}
                </div>
                {quadroMotivo && (
                  <div className={`mt-1.5 text-[10px] ${quadroMotivo === 'portal_sem_conteudo' ? 'text-slate-500' : 'text-amber-400'}`}>
                    {quadroMotivo === 'sessao_expirada' && 'A sessão do ComprasNet expirou — o portal recusou a consulta. Renove a sessão e tente de novo.'}
                    {quadroMotivo === 'falha_proxy' && 'Não foi possível falar com o ComprasNet agora (proxy/rede). Tente novamente em instantes.'}
                    {quadroMotivo === 'falha_relogin' && 'A reconexão ao ComprasNet falhou. Verifique o serviço de automação.'}
                    {quadroMotivo === 'portal_sem_conteudo' && 'O portal respondeu, mas não devolveu o texto destes registros.'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mensagens do portal (chat do pregoeiro) — é onde chega convocação */}
        <div>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Mensagens do portal</div>
          {loadingMsgs && <div className="text-[11px] text-slate-500">Carregando mensagens…</div>}
          {!loadingMsgs && messages && messages.length === 0 && (
            <div className="text-[11px] text-slate-500">Nenhuma mensagem capturada para esta licitação.</div>
          )}
          {!loadingMsgs && !!messages?.length && (
            <div className="space-y-1 max-h-[40vh] overflow-y-auto">
              {messages.map((m: any) => (
                <div
                  key={m.id}
                  className={`text-[11px] rounded px-2 py-1.5 border ${m.mentionsIpcom ? 'bg-amber-500/10 border-amber-600/40 text-amber-100' : 'bg-central-elevado border-central-borda text-slate-300'}`}
                >
                  <div className="flex items-center gap-2 text-[9px] text-slate-500 mb-0.5">
                    {m.mentionsIpcom && <span className="text-amber-400 font-bold">⭐ cita a IPCOM</span>}
                    {m.messageDate && <span>{fmtDateTime(m.messageDate)}</span>}
                    {m.origin && <span className="uppercase">{m.origin}</span>}
                  </div>
                  <div className="whitespace-pre-wrap leading-snug">{m.messageText}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {latestJob && (
          <button
            type="button"
            onClick={() => onViewJob(latestJob)}
            className="w-full text-left bg-central-elevado hover:bg-central-elevado rounded-lg p-2.5 transition-colors"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-300">
                {latestJob.action === 'scrape_proposal' ? 'Sincronização com o portal' : 'Envio da proposta'}
                {ehSimulacao(latestJob) && <span className="text-slate-500"> (teste)</span>}
              </span>
              {js && <span className={`text-[10px] px-2 py-0.5 rounded-full ${js.cls}`}>{js.label}</span>}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {quandoDoJob(latestJob) ? fmtDateTime(quandoDoJob(latestJob)) : ''}
              {latestJob.errorMessage ? ` · ${String(latestJob.errorMessage).slice(0, 90)}` : ''}
            </div>
          </button>
        )}
      </div>
    </details>
  );
}
